import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { Formik, Field } from 'formik';
import { Form, Input, Button, Alert, message } from 'antd';
import {
  BankFilled,
  UserOutlined,
  LoadingOutlined,
  PhoneOutlined,
  MailOutlined,
  LinkOutlined
} from '@ant-design/icons';
import { v4 as uuidv4 } from 'uuid';

import validators from '../../validators/validators';
import { mutations, queries } from '../../graphql/graphql';

import { FormInputField } from '../FormInputField/FormInputField';
import Upload from '../Upload';
import { SelectTemplate } from '../Templates/Select';
import AnswerForSpecifications from '../Specifications/AnswerForSpecifications';
import ButtonWithConfirm from '../UI/ButtonWithConfirm';

const ContractorEdit = ({ match, history }) => {
  const [photos, setPhotos] = useState([]);
  const [specifications, setSpecifications] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [logo, setLogo] = useState({});

  const { params } = match;
  const { id } = params;
  const { loading, data = {} } = useQuery(queries.CONTRACTOR, { variables: { id } });
  const { contractor = {} } = data;

  useEffect(() => {
    if (data && data.contractor) {
      setPhotos(contractor.photos.map(url => ({ uid: uuidv4(), url, status: 'done' })));
      setLogo({ uid: uuidv4(), url: contractor.logo, status: 'done' });
      if (contractor.templateId) setTemplateId(contractor.templateId);
      if (contractor.specifications) setSpecifications(contractor.specifications);
    }
  }, [data]);

  const [CONTRACTOR_UPDATE] = useMutation(mutations.CONTRACTOR_UPDATE);
  const [CONTRACTOR_DELETE] = useMutation(mutations.CONTRACTOR_DELETE);

  const handleSubmitForm = async (values, actions) => {
    const { name, ceo, about, recommendedBy, phone, email, webSite } = values;
    const { setErrors, setSubmitting } = actions;

    CONTRACTOR_UPDATE({
      variables: {
        id,
        name,
        phone: phone.toString(),
        email,
        webSite,
        ceo,
        about,
        logo: logo.url,
        photos: photos.map(p => p.url),
        recommendedBy,
        templateId,
        specifications
      }
    }).then(
      res => {
        message.success('Builder successfully updated');
        setSubmitting(true);
        const { data } = res;
        const { contractorUpdate } = data;
        history.push(`/contractor/${contractorUpdate.id}`);
      },
      err => {
        const errors = {};
        err.graphQLErrors.map(x => {
          if (x.message.includes('name')) {
            errors.name = 'Company name has already been taken.';
          }
        });
        setSubmitting(false);
        setErrors(errors);
      }
    );
  };

  const onChangeLogo = photos => {
    if (!photos.length) return;
    setLogo(photos[0]);
  };

  const onRemove = e => {
    CONTRACTOR_DELETE({
      variables: {
        id
      }
    }).then(
      res => {
        message.success('Builder successfully deleted');
        history.push(`/admin/contractors`);
      },
      err => {
        err.graphQLErrors.map(x => {
          if (x.message.includes('plans')) {
            message.error(x.message);
          }
        });
      }
    );
  };

  return !loading ? (
    <div
      style={{
        display: 'block',
        width: '100vw',
        height: '100vh',
        padding: 0,
        background: '#ffffff'
      }}
    >
      <Formik
        initialValues={{
          name: contractor.name,
          phone: Number(contractor.phone) || NaN,
          email: contractor.email || '',
          webSite: contractor.webSite || '',
          ceo: contractor.ceo,
          about: contractor.about,
          recommendedBy: contractor.recommendedBy
        }}
        validationSchema={validators.contractor.mainSchema}
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={(values, actions) => handleSubmitForm(values, actions)}
        render={formikProps => {
          const { errors, isSubmitting, handleSubmit, submitForm } = formikProps;

          return (
            <div style={{ padding: 50 }}>
              {Object.keys(errors).length > 0 && (
                <Alert
                  message={
                    errors.about ||
                    errors.ceo ||
                    errors.name ||
                    errors.logo ||
                    errors.email ||
                    errors.phone ||
                    errors.webSite
                  }
                  type="error"
                  showIcon
                  style={{ marginBottom: '1.05rem', maxWidth: '300px' }}
                />
              )}
              <Form onSubmit={handleSubmit}>
                <Field
                  InputType={Input}
                  component={FormInputField}
                  prefix={<BankFilled />}
                  name="name"
                  placeholder="Company name"
                  hideErrorMessage={true}
                />
                <Field
                  InputType={Input}
                  component={FormInputField}
                  prefix={<UserOutlined />}
                  name="ceo"
                  placeholder="CEO"
                  hideErrorMessage={true}
                />
                <Field
                  InputType={Input}
                  type="number"
                  component={FormInputField}
                  prefix={<PhoneOutlined />}
                  name="phone"
                  placeholder="Phone number (e.g. 6503330000)"
                  hideErrorMessage={true}
                />
                <Field
                  InputType={Input}
                  component={FormInputField}
                  prefix={<MailOutlined />}
                  name="email"
                  placeholder="email"
                  hideErrorMessage={true}
                />
                <Field
                  InputType={Input}
                  component={FormInputField}
                  prefix={<LinkOutlined />}
                  name="webSite"
                  placeholder="https://web.site..."
                  hideErrorMessage={true}
                />
                <Field
                  InputType={Upload}
                  component={FormInputField}
                  name="logo"
                  onChange={onChangeLogo}
                  items={logo && logo.uid ? [logo] : []}
                  placeholder="Upload logo"
                  single={true}
                />
                <Field
                  InputType={Input.TextArea}
                  component={FormInputField}
                  name="about"
                  placeholder="About"
                  hideErrorMessage={true}
                />
                <Field
                  InputType={Upload}
                  component={FormInputField}
                  name="photos"
                  onChange={setPhotos}
                  items={photos}
                  aspect={1.3}
                  placeholder="Upload photos"
                  crop={true}
                />
                <Field
                  InputType={SelectTemplate}
                  component={FormInputField}
                  name="templateId"
                  onChange={setTemplateId}
                  value={templateId}
                />
                {templateId && (
                  <Field
                    InputType={AnswerForSpecifications}
                    component={FormInputField}
                    name="specifications"
                    onChange={setSpecifications}
                    value={specifications}
                    templateId={templateId}
                    prefix="contractor"
                  />
                )}
                <Form.Item>
                  <ButtonWithConfirm
                    title="Are you sure you want to remove the contractor?"
                    type="primary"
                    danger={true}
                    onConfirm={onRemove}
                    text="Delete"
                  />
                </Form.Item>
                <Form.Item style={{ marginBottom: 'unset' }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={isSubmitting}
                    onClick={submitForm}
                  >
                    Update
                  </Button>
                </Form.Item>
              </Form>
            </div>
          );
        }}
      />
    </div>
  ) : (
    <LoadingOutlined style={{ justifyContent: 'center', alignItems: 'center', display: 'flex' }} />
  );
};

const mapStateToProps = state => {
  return {
    user: state.auth.user
  };
};

const mapDispatchToProps = dispatch => {
  return bindActionCreators({}, dispatch);
};

ContractorEdit.propTypes = {
  user: PropTypes.object.isRequired
};

const connectedEdit = connect(mapStateToProps, mapDispatchToProps)(ContractorEdit);

export default connectedEdit;
